import * as React from 'react'
import * as ReactDOM from 'react-dom'

import { GlobalEventInterface, GlobalEvents } from '../../../Other/GlobalEvents'
import Button, { ButtonType } from '../Button/Button'
import { TestableInterface } from '../interfaces/TestableInterface'
import { makeTranslatable, TranslatableType } from '../interfaces/Translatable'
import './DropdownButton.scss'

export interface ButtonProps extends TestableInterface {
  label: TranslatableType
  automation?: string
  disabled?: boolean
  type?: ButtonType
  selected?: boolean
  hideIfNoButtons?: boolean
  noMenuPositioning?: boolean
}

export interface ButtonState {
  menuOpen: boolean
  intermediateState: boolean // A state between the click event and the execution of all click listeners
}

export class DropdownButton extends React.Component<ButtonProps, ButtonState> {
  private e: GlobalEventInterface

  public state = {
    menuOpen: false,
    intermediateState: false,
  }

  public static defaultProps: ButtonProps = {
    label: 'Button',
    automation: '',
    disabled: false,
    type: ButtonType.accept,
    selected: false,
    hideIfNoButtons: false,
    noMenuPositioning: false,
  }

  public componentDidMount() {
    const self = this
    this.e = GlobalEvents.Click(() => {
      if (!self.state.intermediateState) {
        self.setState({ menuOpen: false })
      }
    })
  }

  public componentWillUnmount() {
    GlobalEvents.unBind(this.e)
  }

  public componentDidUpdate() {
    // This sets correct x-position for the button menu so that the whole menu is on screen
    const menuNode = ReactDOM.findDOMNode(this.refs.menu)
    const baseNode = ReactDOM.findDOMNode(this.refs.base)
    const windowDivs = window.document.getElementsByClassName('window')
    // this was needed to set menu position correctly in modals
    const windowWidth = windowDivs && windowDivs[0] ? windowDivs[0].getBoundingClientRect().width : window.innerWidth
    const menuBounding = menuNode && menuNode.getBoundingClientRect()
    const baseBounding = baseNode && baseNode.getBoundingClientRect()
    if (menuBounding && menuBounding.right > windowWidth) {
      menuNode.style.right = '2px'
    } else if (baseBounding && menuBounding && !this.props.noMenuPositioning) {
      // note that baseBounding.right is measured from the left edge of the window to the right edge of the element
      // and style.right is measured from the right edge of the element to
      // a) the right side of window (position: fixed)
      // b) the right side of parent div (position: absolute)
      const offset = [ButtonType.normal, ButtonType.accept].includes(this.props.type) ? 20 : 10
      menuNode.style.right = windowWidth - baseBounding.right - offset + 'px'
    }
  }

  private toggleMenu = () => {
    this.setState({ menuOpen: !this.state.menuOpen })
  }

  private activateIntermediateState = () => {
    /**
     * With this we can distinguish this instance from the others in the click listener
     */
    this.setState({ intermediateState: true })
    setTimeout(() => {
      this.setState({ intermediateState: false })
    }, 0)
  }

  public render() {
    const { menuOpen } = this.state
    const { disabled, selected } = this.props
    const classes = ['Button', 'DropdownButton', ' button-menu-div', ButtonType[this.props.type]]
    if (disabled) {
      classes.push('disabled')
    }
    if (selected) {
      classes.push('selected')
    }

    if (this.props.hideIfNoButtons) {
      if (React.Children.toArray(this.props.children).filter((child) => child.type.name === Button.name).length < 1) {
        return null
      }
    }

    return (
      <div className={classes.join(' ')} ref={'base'}>
        <div
          data-automation={this.props.automation}
          className={['menu-base dropdown', ButtonType[this.props.type]].join(' ')}
          onClick={(e) => {
            if (!disabled) {
              this.toggleMenu()
              this.activateIntermediateState()
            }
          }}
        >
          {makeTranslatable(this.props.label)}
        </div>
        {menuOpen && (
          <div className="button-menu" onClick={this.toggleMenu} ref={'menu'}>
            {this.props.children}
          </div>
        )}
      </div>
    )
  }
}
